/**
 * Color Manipulator
 * Per R-4: Focused class for color manipulation operations
 * Handles brightness, saturation, hue rotation, inversion, and desaturation
 */
import { clamp } from '../../utils/index.js';
import { ColorConverter } from './ColorConverter.js';
/**
 * Color manipulation utilities
 * Per R-4: Single Responsibility - color manipulation only
 */
export class ColorManipulator {
    /**
     * Adjust brightness of a color
     * @param amount -100 to 100 (negative = darker, positive = lighter)
     */
    static adjustBrightness(hex, amount) {
        // BUG-005 (2026-07-18 audit): never mutate the conversion result — the
        // converter caches these objects, and in-place edits poisoned the cache
        const { h, s, v } = ColorConverter.hexToHsv(hex);
        return ColorConverter.hsvToHex(h, s, clamp(v + amount, 0, 100));
    }
    /**
     * Adjust saturation of a color
     * @param amount -100 to 100 (negative = less saturated, positive = more saturated)
     */
    static adjustSaturation(hex, amount) {
        // BUG-005: build new values instead of mutating the cached object
        const { h, s, v } = ColorConverter.hexToHsv(hex);
        return ColorConverter.hsvToHex(h, clamp(s + amount, 0, 100), v);
    }
    /**
     * Rotate hue of a color
     * @param degrees Amount to rotate hue (can be negative or positive)
     */
    static rotateHue(hex, degrees) {
        // BUG-005: build new values instead of mutating the cached object
        const { h, s, v } = ColorConverter.hexToHsv(hex);
        // Normalize to 0-360 to handle negative values properly
        return ColorConverter.hsvToHex((((h + degrees) % 360) + 360) % 360, s, v);
    }
    /**
     * Rotate hue of a color in CIE LCh space (perceptual hue rotation).
     *
     * Unlike {@link rotateHue} (HSV), rotating in LCh preserves perceived
     * lightness and chroma, so the "ideal" hue offsets used by harmony math
     * (e.g. a tetradic +90°) stay perceptually meaningful. Out-of-gamut results
     * are clamped by the LCh→RGB conversion.
     *
     * @param degrees Amount to rotate hue (can be negative or positive)
     */
    static rotateHueLch(hex, degrees) {
        const { L, C, h } = ColorConverter.hexToLch(hex);
        return ColorConverter.lchToHex(L, C, (((h + degrees) % 360) + 360) % 360);
    }
    /**
     * Invert a color (create complementary color)
     */
    static invert(hex) {
        const rgb = ColorConverter.hexToRgb(hex);
        return ColorConverter.rgbToHex(255 - rgb.r, 255 - rgb.g, 255 - rgb.b);
    }
    /**
     * Desaturate a color (convert to grayscale)
     */
    static desaturate(hex) {
        const hsv = ColorConverter.hexToHsv(hex);
        return ColorConverter.hsvToHex(hsv.h, 0, hsv.v);
    }
}
//# sourceMappingURL=ColorManipulator.js.map